import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Wrench, Plus, Pencil, Trash2, ExternalLink, Send, MailCheck } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", body: "", version: "", category: "feature", published: true, published_at: "", notify_subscribers: true };

const CATEGORY_META = {
  feature: { label: "New", icon: Sparkles, bg: "bg-[#3A5A4A]", text: "text-white" },
  improvement: { label: "Improved", icon: Plus, bg: "bg-[#E8DCC8]", text: "text-[#3A5A4A]" },
  fix: { label: "Fixed", icon: Wrench, bg: "bg-[#F3EFEA]", text: "text-[#7A736E]" },
};

export default function AdminChangelogTab() {
  const [entries, setEntries] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/changelog")
    .then((r) => setEntries(r.data))
    .catch(() => setEntries([]));

  useEffect(() => { load(); }, []);

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        body: form.body,
        version: form.version || null,
        category: form.category,
        published: form.published,
        published_at: form.published_at || null,
        notify_subscribers: !!form.notify_subscribers,
      };
      let resp;
      if (editingId) {
        resp = await api.put(`/admin/changelog/${editingId}`, payload);
      } else {
        resp = await api.post("/admin/changelog", payload);
      }
      const ann = resp.data?.announce;
      if (ann?.sent > 0) {
        toast.success(`Saved. Emailed ${ann.sent} subscriber${ann.sent === 1 ? "" : "s"}.`);
      } else if (ann?.eligible === 0) {
        toast.success("Saved. No opted-in subscribers to email.");
      } else if (editingId) {
        toast.success("Entry updated");
      } else {
        toast.success("Entry posted");
      }
      reset();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const edit = (e) => {
    setForm({
      title: e.title || "",
      body: e.body || "",
      version: e.version || "",
      category: e.category || "feature",
      published: e.published !== false,
      published_at: e.published_at ? e.published_at.slice(0, 10) : "",
      notify_subscribers: !e.email_announced_at, // default OFF if already announced
    });
    setEditingId(e.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const announce = async (e) => {
    const already = !!e.email_announced_at;
    const msg = already
      ? `This was already emailed to ${e.email_announce_count || 0} subscribers on ${new Date(e.email_announced_at).toLocaleDateString()}.\n\nRe-send anyway? (forces a second email — use sparingly)`
      : `Send this release note to all opted-in subscribers now?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/admin/changelog/${e.id}/announce`, { force: already });
      if (data.ok && data.result) {
        toast.success(`Emailed ${data.result.sent} subscriber${data.result.sent === 1 ? "" : "s"}${data.result.failed ? ` (${data.result.failed} failed)` : ""}.`);
      } else if (!data.ok && data.already_sent_at) {
        toast.info("Already sent. Use the re-send confirm to override.");
      } else {
        toast.success("Done");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Announce failed");
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/changelog/${e.id}`);
      toast.success("Entry deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-changelog-tab">
      <div className="hc-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="font-display text-xl">{editingId ? "Edit release note" : "Post a new release note"}</h2>
            <p className="text-xs text-[#7A736E] mt-1">Published notes appear on the public /changelog page newest first.</p>
          </div>
          <a
            href="/changelog"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-[#3A5A4A] hover:underline"
            data-testid="changelog-view-public"
          >
            View public page <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. 14-day Premium trial for new signups"
              className="rounded-xl mt-1.5"
              data-testid="changelog-input-title"
            />
          </div>
          <div>
            <Label>Version (optional)</Label>
            <Input
              value={form.version}
              onChange={(e) => setForm({ ...form, version: e.target.value })}
              placeholder="v1.21.0"
              className="rounded-xl mt-1.5"
              data-testid="changelog-input-version"
            />
          </div>
        </div>

        <div className="mt-4">
          <Label>Body</Label>
          <p className="text-xs text-[#7A736E] mb-1.5">Plain text. New lines starting with &quot;- &quot; render as bullets.</p>
          <Textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder={"What changed and why it matters to caregivers.\n- Bullet one\n- Bullet two"}
            rows={6}
            className="rounded-xl mt-1.5 font-mono text-sm"
            data-testid="changelog-input-body"
          />
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div>
            <Label>Category</Label>
            <div className="flex gap-2 mt-1.5">
              {Object.entries(CATEGORY_META).map(([key, meta]) => (
                <button
                  key={key}
                  onClick={() => setForm({ ...form, category: key })}
                  className={`flex-1 rounded-xl px-3 py-2 text-sm border transition ${
                    form.category === key
                      ? `${meta.bg} ${meta.text} border-transparent`
                      : "bg-white border-[#EBE5DF] text-[#7A736E] hover:border-[#3A5A4A]"
                  }`}
                  data-testid={`changelog-cat-${key}`}
                >
                  {meta.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={form.published_at}
              onChange={(e) => setForm({ ...form, published_at: e.target.value })}
              className="rounded-xl mt-1.5"
              data-testid="changelog-input-date"
            />
          </div>
          <div>
            <Label>Visibility</Label>
            <div className="flex gap-2 mt-1.5">
              <button
                onClick={() => setForm({ ...form, published: true })}
                className={`flex-1 rounded-xl px-3 py-2 text-sm border transition ${
                  form.published ? "bg-[#3A5A4A] text-white border-transparent" : "bg-white border-[#EBE5DF] text-[#7A736E]"
                }`}
                data-testid="changelog-publish-on"
              >
                Published
              </button>
              <button
                onClick={() => setForm({ ...form, published: false })}
                className={`flex-1 rounded-xl px-3 py-2 text-sm border transition ${
                  !form.published ? "bg-[#7A736E] text-white border-transparent" : "bg-white border-[#EBE5DF] text-[#7A736E]"
                }`}
                data-testid="changelog-publish-off"
              >
                Draft
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 p-4 rounded-2xl bg-[#F3EFEA] border border-[#EBE5DF] flex items-start gap-3">
          <input
            type="checkbox"
            id="notify-subscribers"
            checked={!!form.notify_subscribers}
            onChange={(e) => setForm({ ...form, notify_subscribers: e.target.checked })}
            className="mt-1 w-4 h-4 accent-[#3A5A4A]"
            data-testid="changelog-notify-toggle"
          />
          <label htmlFor="notify-subscribers" className="flex-1 cursor-pointer">
            <p className="text-sm font-medium text-[#3D3935]">Email opted-in subscribers when this is published</p>
            <p className="text-xs text-[#7A736E] mt-0.5">
              Sends a one-time announcement to all users who haven&apos;t opted out of product updates. Already-announced entries won&apos;t be re-emailed automatically.
            </p>
          </label>
        </div>

        <div className="flex gap-2 mt-5">
          <Button
            onClick={submit}
            disabled={busy}
            className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]"
            data-testid="changelog-submit"
          >
            {busy ? "Saving…" : editingId ? "Update entry" : (form.notify_subscribers && form.published ? "Post & email subscribers" : "Post release note")}
          </Button>
          {editingId && (
            <Button onClick={reset} variant="outline" className="rounded-full border-[#EBE5DF]" data-testid="changelog-cancel-edit">
              Cancel edit
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4" data-testid="changelog-admin-list">
        <h3 className="font-display text-lg">All entries ({entries === null ? "…" : entries.length})</h3>
        {entries === null && <p className="text-[#7A736E]">Loading…</p>}
        {entries !== null && entries.length === 0 && (
          <div className="hc-card p-8 text-center text-[#7A736E]">No entries yet. Post your first release note above.</div>
        )}
        {(entries || []).map((e) => {
          const cat = CATEGORY_META[e.category] || CATEGORY_META.feature;
          const Icon = cat.icon;
          return (
            <div key={e.id} className="hc-card p-5 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`inline-flex items-center gap-1.5 rounded-full ${cat.bg} ${cat.text} px-2.5 py-1 text-xs font-medium`}>
                    <Icon className="w-3 h-3" />
                    {cat.label}
                  </span>
                  {e.version && <span className="text-xs text-[#7A736E] font-mono bg-[#F3EFEA] px-2 py-0.5 rounded-full">{e.version}</span>}
                  {!e.published && <span className="text-xs uppercase tracking-wider text-[#7A736E] border border-[#EBE5DF] px-2 py-0.5 rounded-full">Draft</span>}
                  {e.published_at && (
                    <span className="text-xs text-[#7A736E]">
                      {new Date(e.published_at).toLocaleDateString()}
                    </span>
                  )}
                  {e.email_announced_at && (
                    <span
                      className="inline-flex items-center gap-1 text-xs text-[#3A5A4A] bg-[#D8E2D8] px-2 py-0.5 rounded-full"
                      title={`Emailed ${e.email_announce_count || 0} subscribers on ${new Date(e.email_announced_at).toLocaleString()}`}
                      data-testid={`changelog-sent-badge-${e.id}`}
                    >
                      <MailCheck className="w-3 h-3" />
                      Emailed {e.email_announce_count || 0}
                    </span>
                  )}
                </div>
                <p className="font-display text-lg leading-tight truncate">{e.title}</p>
                <p className="text-sm text-[#7A736E] mt-1 line-clamp-2 whitespace-pre-wrap">{e.body}</p>
              </div>
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                {e.published && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full border-[#EBE5DF] text-[#3A5A4A]"
                    onClick={() => announce(e)}
                    data-testid={`changelog-announce-${e.id}`}
                  >
                    <Send className="w-3.5 h-3.5 mr-1" />
                    {e.email_announced_at ? "Re-send" : "Email subs"}
                  </Button>
                )}
                <Button size="sm" variant="outline" className="rounded-full border-[#EBE5DF]" onClick={() => edit(e)} data-testid={`changelog-edit-${e.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="rounded-full border-[#EBE5DF] text-[#C87B67]" onClick={() => remove(e)} data-testid={`changelog-delete-${e.id}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
